import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPaw,
    faMapMarkerAlt,
    faPhoneAlt,
    faClock,
    faUser,
    faCheckCircle,
    faDollarSign,
    faHistory,
    faInfoCircle,
    faStar,
    faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { CURRENCY_CODE, WALK_STATUS } from "../../config/constants";
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/api';
import { toast } from 'react-toastify';
import LoadingLogo from '../../components/Loading';
import { capitalizeWords } from '../../config/utility';
import StarRating from '../../components/StarRating';

const SingleWalk = () => {
    const { walk_id } = useParams();
    const [walk, setWalk] = useState(null); // null initially
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate(); // For redirecting

    const statusStyles = {
        requested: "bg-yellow-100 text-yellow-800",
        walker_assigned: "bg-blue-100 text-blue-800",
        in_progress: "bg-blue-500 text-white",
        completed: "bg-green-100 text-green-800",
        canceled: "bg-red-100 text-red-800",
    };

    useEffect(() => {
        fetchWalk();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchWalk = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/walks/${walk_id}`);
            setWalk(response.data);
        } catch (error) {
            if (error.response && error.response.status === 404) {
                // Redirect to a Not Found page
                navigate('/not-found');

            } else {
                toast.error('Failed to fetch walk history.');
            }
        } finally {
            setLoading(false);
        }
    };
    if (loading) {
        return <LoadingLogo />;
    }

    if (!walk) {
        return navigate('/not-found');
    }

    // Destructure safely from walk
    const {
        id,
        status,
        duration,
        clean_pet,
        emergency_no,
        cancel_reason,
        scheduled_at,
        additional_info,
        charges,
        walker,
        user,
        pickup_location,
        pets,
        tools,
        created_at,
        status_time,
    } = walk;

    return (
        <div className="w-full transform overflow-hidden rounded-lg p-2 sm:p-16 text-left align-middle transition-all">

            {/* Header */}
            <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-semibold text-gray-900">
                     Walk Detail - ID #{id}
                </h3>
            </div>

            {/* Status Badge */}
            <div
                className={`px-4 py-1 text-center rounded-full text-sm font-semibold w-max ${statusStyles[status] || "bg-gray-100 text-gray-800"
                    }`}
            >
                {status.replace("_", " ").toUpperCase()}
            </div>

            {/* Status Timeline */}
            {status_time && Object.keys(status_time).length > 0 && (
                <div className="mt4 sm:mt-6 bg-gray-50 p-2 sm:p-4 rounded-lg shadow-inner">
                    <h4 className="text-gray-700 font-semibold mb-3 flex items-center">
                        <FontAwesomeIcon icon={faHistory} className="mr-2 text-blue-600" />
                        Status Timeline
                    </h4>
                    <div className="border-l-2 border-gray-300 pl-2 sm:pl-4 space-y-3">

                        {Object.entries(WALK_STATUS).map(([key, label]) =>
                            status_time[label] ? (
                                <div key={label} className="flex items-center space-x-3">
                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600" />
                                    <span className="font-semibold text-xs sm:text-base">{capitalizeWords(key)} </span>
                                    <span className="text-gray-600 text-xs sm:text-sm">
                                        {new Date(status_time[label]).toLocaleString()}
                                    </span>
                                </div>
                            ) : null
                        )}
                    </div>
                </div>
            )}

            {/* Map Section */}
            {/* <motion.div
          key={id + ",map"}
          className="mt-6"
          whileHover={{ scale: 1.01 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <WalkMapStatus walk={walk} />
        </motion.div> */}

            {/* Main Details */}
            <div className="mt-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">


                    <div className="flex flex-col space-y-4 rounded-lg shadow-lg bg-gray-100 p-4 sm:p-10 max-w-lg mx-auto">
                        {/* Heading */}
                        <div className="text-center border-b pb-3">
                            <h3 className="text-lg sm:text-2xl font-bold text-blue-700">
                                <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-600" />
                                Customer Details
                            </h3>
                        </div>

                        {/* User Profile */}
                        <div className="flex flex-col items-center space-y-3">
                            <img
                                src={user?.profile_photo || "https://via.placeholder.com/80"}
                                alt={user?.name || "User Profile"}
                                className="w-20 h-20 rounded-full border-4 border-gray-300 shadow-md"
                            />
                            <div className="text-center">
                                <span className="text-base sm:text-lg font-semibold">{user?.name || "N/A"}</span>
                                <p className="text-xs sm:text-sm text-gray-500">{user?.email || "No Email"}</p>
                            </div>
                        </div>

                        {/* User Info */}
                        <div className="space-y-3 text-gray-700 text-xs sm:text-base">
                            {/* Phone Number */}
                            <div className="flex items-center space-x-2">
                                <FontAwesomeIcon icon={faPhoneAlt} className="text-green-600" />
                                <span>
                                    <strong>Phone:</strong> {user?.phone || "N/A"}
                                </span>
                            </div>

                            {/* Join Date */}
                            <div className="flex items-center space-x-2">
                                <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-600" />
                                <span>
                                    <strong>Joined:</strong> {user?.join_at || "Unknown"}
                                </span>
                            </div>

                            {/* Location */}
                            {user?.pin_location && (
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600" />
                                    <span>
                                        <strong>Pin Location:</strong> {user.pin_location.address || "N/A"}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>


                    {walker && (
                        <div className="flex flex-col space-y-4 rounded-lg shadow-lg bg-gray-300 p-4 sm:p-10 max-w-full sm:max-w-lg mx-auto">
                            {/* Heading */}
                            <div className="text-center border-b pb-3">
                                <h3 className="text-lg sm:text-2xl font-bold text-purple-700">
                                    <FontAwesomeIcon icon={faUser} className="mr-2 text-purple-600" />
                                    Assigned Walker
                                </h3>
                            </div>

                            {/* Walker Profile */}
                            <div className="flex flex-col items-center space-y-3">
                                <img
                                    src={walker?.profile_photo || "https://via.placeholder.com/80"}
                                    alt={walker?.name || "Walker Profile"}
                                    className="w-20 h-20 rounded-full border-4 border-gray-300 shadow-md"
                                />
                                <div className="text-center">
                                    <span className="text-base sm:text-lg font-semibold">{walker?.name || "N/A"}</span>
                                    <p className="text-xs sm:text-sm text-gray-500">{walker?.email || "No Email"}</p>
                                </div>
                            </div>

                            {/* Walker Info */}
                            <div className="space-y-3 text-gray-700 text-xs sm:text-base">
                                {/* Phone Number */}
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faPhoneAlt} className="text-green-600" />
                                    <span>
                                        <strong>Phone:</strong> {walker?.phone || "N/A"}
                                    </span>
                                </div>

                                {/* Join Date */}
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faCalendarAlt} className="text-purple-600" />
                                    <span>
                                        <strong>Joined:</strong> {walker?.join_at || "Unknown"}
                                    </span>
                                </div>

                                {/* Average Rating */}
                                <div className="flex items-center space-x-2 ">
                                    <FontAwesomeIcon icon={faStar} className="text-yellow-500" />
                                    <span>
                                        <strong>Walk Rating:</strong>
                                    </span>


                                    <StarRating rating={walker?.avg_rating} />
                                    <span>
                                        <strong>({walker?.avg_rating})</strong>
                                    </span>

                                </div>


                                {/* Location */}
                                {walker?.pin_location && (
                                    <div className="flex items-center space-x-2">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600" />
                                        <span>
                                            <strong>Current Location:</strong> {walker.pin_location.address || "N/A"}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                </div>

                {walker && (
                    <div className="flex items-center space-x-2 text-gray-700">
                        <FontAwesomeIcon icon={faUser} className="text-purple-600" />
                        <span>
                            Assigned Walker: <strong>{walker.name}</strong>
                        </span>
                    </div>
                )}

                <div className="flex items-center space-x-2 text-gray-700">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-600" />
                    <span>
                        Pickup Location: <strong>{pickup_location?.address || "N/A"}</strong>
                    </span>
                </div>

                <div className="flex items-center space-x-2 text-gray-700">
                    <FontAwesomeIcon icon={faClock} className="text-purple-600" />
                    <span>
                        Duration: <strong>{duration || "N/A"}</strong>
                    </span>
                </div>

                <div className="flex items-center space-x-2 text-gray-700">
                    <FontAwesomeIcon icon={faPhoneAlt} className="text-blue-600" />
                    <span>
                        Emergency Contact: <strong>{emergency_no || "N/A"}</strong>
                    </span>
                </div>


                <div className="mt-6 flex items-center space-x-2 text-gray-700">
                    <FontAwesomeIcon
                        icon={faPaw}
                        className={clean_pet ? "text-green-600" : "text-gray-400"}
                    />
                    <span>
                        {clean_pet
                            ? "Pet Cleaning Included"
                            : "Pet Cleaning Not Included"}
                    </span>
                </div>



                {/* Pets */}
                {pets && pets.length > 0 && (

                    <div className="mt-6 space-y-4">
                        <h4 className="text-gray-700 font-semibold">Pets</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                            {pets.map((pet) => (
                                <div
                                    key={pet.id}
                                    className="bg-gray-100 p-3 rounded-lg shadow-md text-center"
                                >
                                    <img
                                        src={pet.primary_image_url}
                                        alt={pet.name}
                                        className="w-16 h-16 rounded-full mx-auto object-cover shadow-md mb-2"
                                    />
                                    <h5 className="text-gray-800 font-semibold">
                                        {pet.name}
                                    </h5>
                                    <p className="text-sm text-gray-500">
                                        {pet.type.name}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tools */}
                {tools && tools.length > 0 && (
                    <div className="mt-6 space-y-4">
                        <h4 className="text-gray-700 font-semibold">Tools</h4>
                        <div className="space-y-2">
                            {tools.map((tool) => (

                                <div
                                    key={tool.id}
                                    className="flex justify-between items-center bg-gray-50 p-3 rounded-lg"
                                >
                                    <span className="font-semibold text-gray-800">
                                        {tool.name}
                                    </span>
                                    <span className="text-gray-600">
                                        {tool.price} {CURRENCY_CODE}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Additional Information */}
                {additional_info && (
                    <div className="mt-6 bg-gray-50 p-4 rounded-lg shadow-inner">
                        <h4 className="text-gray-700 font-semibold">
                            Additional Information
                        </h4>
                        <p className="text-gray-600">{additional_info}</p>
                    </div>
                )}

                {cancel_reason && (
                    <div className="text-sm text-red-600">
                        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
                        <span>Cancel Reason: {cancel_reason}</span>
                    </div>
                )}


            </div>

            {/* Charges */}
            <div className="bg-gray-100 p-6 rounded-lg shadow-md mt-6">
                <h2 className="text-xl font-semibold mb-4">Cost Breakdown</h2>
                <div className="space-y-3">
                    {Object.entries(charges)
                        .filter(([key, value]) => parseFloat(value) > 0 && key !== "total")
                        .map(([key, value], index) => (
                            <div key={index} className="flex justify-between">
                                <span className="text-gray-600 capitalize">
                                    <FontAwesomeIcon icon={faDollarSign} className="mr-2" />
                                    {key.replace(/_/g, " ")}
                                </span>
                                <span className="text-gray-800 font-semibold">
                                    {parseFloat(value).toFixed(2)} {CURRENCY_CODE}
                                </span>
                            </div>
                        ))}
                </div>
                <div className="mt-4 text-lg font-bold flex justify-between border-t pt-3">
                    <span className="text-purple-600">Total:</span>
                    <span className="text-gray-800">
                        {parseFloat(charges.total).toFixed(2)} {CURRENCY_CODE}
                    </span>
                </div>
            </div>

            {/* Close Button */}

        </div>
    );
};

export default SingleWalk;
